import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { db } from '../firebase';
import { doc, setDoc, collection, query, where, getDocs, getDoc } from 'firebase/firestore';
import Flashcard from './Flashcard';
import CharacterOrigin from './CharacterOrigin';
import { playSound, SOUNDS, vibrate } from '../utils/audio';
import { tts } from '../utils/tts';
import './Deck.css';
import '../styles/Modal.css';

const getRandomCards = (allCards) => {
  return [...allCards].sort(() => 0.5 - Math.random());
};

function SortableItem({ id }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <span className="side-label">
        {id.charAt(0).toUpperCase() + id.slice(1)}
      </span>
      <div className="drag-handle">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="4" y1="9" x2="20" y2="9"></line>
          <line x1="4" y1="15" x2="20" y2="15"></line>
        </svg>
      </div>
    </li>
  );
}

const Deck = ({
  isOrderModalOpen,
  setIsOrderModalOpen,
  userId,
  currentSet,
  charactersUpdated,
  onManageCharacters,
  showResult,
  setShowResult
}) => {
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [currentSideIndex, setCurrentSideIndex] = useState(0);
  const [sideOrder, setSideOrder] = useState(['hanzi', 'pinyin', 'translation']);
  const [tempSideOrder, setTempSideOrder] = useState(sideOrder);
  const [options, setOptions] = useState([]);
  const [points, setPoints] = useState(0);
  const [showCardFeedback, setShowCardFeedback] = useState(false);
  const [showCharacterOrigin, setShowCharacterOrigin] = useState(false);
  const [currentCardAnswers, setCurrentCardAnswers] = useState({ side2: null, side3: null });
  const [currentTestCards, setCurrentTestCards] = useState([]);
  const [allCharacters, setAllCharacters] = useState([]);
  const [incorrectAnswers, setIncorrectAnswers] = useState([]);
  const [showIncorrectAnswers, setShowIncorrectAnswers] = useState(false);
  const [isPracticeMode, setIsPracticeMode] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const modalRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const loadUserSettings = async () => {
      try {
        const docRef = doc(db, 'userSettings', userId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists() && docSnap.data().sideOrder && Array.isArray(docSnap.data().sideOrder)) {
          setSideOrder(docSnap.data().sideOrder);
          setTempSideOrder(docSnap.data().sideOrder);
        }
      } catch (settingsError) {
        console.error('Error loading user settings:', settingsError);
      }
    };

    if (userId) {
      loadUserSettings();
    }
  }, [userId]);

  // Sync tempSideOrder when modal opens
  useEffect(() => {
    if (isOrderModalOpen) {
      setTempSideOrder(Array.isArray(sideOrder) ? sideOrder : ['hanzi', 'pinyin', 'translation']);
    }
  }, [isOrderModalOpen, sideOrder]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const fetchPromises = currentSet.map(set => {
          if (set.isPublic) {
            return getDocs(
              query(
                collection(db, 'publicSetCharacters'),
                where('publicSetId', '==', set.id)
              )
            );
          }

          // If it's a demo set, don't filter by userId
          if (set.isDemo) {
            return getDocs(
              query(
                collection(db, 'characters'),
                where('setId', '==', set.id)
              )
            );
          }
          // For regular sets, include userId filter
          return getDocs(
            query(
              collection(db, 'characters'),
              where('userId', '==', userId),
              where('setId', '==', set.id)
            )
          );
        });

        const snapshots = await Promise.all(fetchPromises);
        const chars = snapshots.flatMap(snapshot =>
          snapshot.docs.map(characterDoc => ({
            id: characterDoc.id,
            ...characterDoc.data()
          }))
        );

        setAllCharacters(chars);
        setCurrentTestCards(getRandomCards(chars));
        setCurrentCardIndex(0);
        setCurrentSideIndex(0);
        setPoints(0);
        setShowResult(false);
        setShowCardFeedback(false);
        setCurrentCardAnswers({ side2: null, side3: null });
        setIncorrectAnswers([]);
        setShowIncorrectAnswers(false);
        setIsPracticeMode(false);
      } catch (fetchError) {
        console.error('Error fetching characters:', fetchError);
      }
    };

    if (currentSet?.length > 0) {
      fetchData();
    }
  }, [userId, currentSet, charactersUpdated, setShowResult]);

  const closeModal = useCallback(() => {
    setIsOrderModalOpen(false);
    setTempSideOrder(sideOrder);
  }, [setIsOrderModalOpen, sideOrder]);

  const getRandomOptions = useCallback((correctAnswer, type) => {
    const generatedOptions = [correctAnswer];
    const availableAnswers = allCharacters
      .filter(card => card[type] !== correctAnswer)
      .map(card => card[type]);

    while (generatedOptions.length < 3 && availableAnswers.length > 0) {
      const randomIndex = Math.floor(Math.random() * availableAnswers.length);
      const option = availableAnswers[randomIndex];
      if (!generatedOptions.includes(option)) {
        generatedOptions.push(option);
      }
    }

    return generatedOptions.sort(() => Math.random() - 0.5);
  }, [allCharacters]);

  const isExplanationOption = (option) => {
    if (sideOrder[currentSideIndex] !== 'translation') return false;
    return allCharacters.some(card =>
      card.translation === option && card.translationIsExplanation
    );
  };

  const isTranslationExplanation = (card, side = 'translation') =>
    side === 'translation' && card?.translationIsExplanation;

  useEffect(() => {
    if (currentSideIndex > 0) {
      const currentCard = currentTestCards[currentCardIndex];
      const currentSide = sideOrder[currentSideIndex];
      setOptions(getRandomOptions(currentCard[currentSide], currentSide));
    }
  }, [currentSideIndex, currentCardIndex, sideOrder, currentTestCards, getRandomOptions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        closeModal();
      }
    };

    if (isOrderModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOrderModalOpen, closeModal]);

  useEffect(() => {
    if (showCardFeedback && currentTestCards[currentCardIndex]) {
      // Small timeout to ensure visual transition finishes
      const timer = setTimeout(() => {
        tts.speak(
          currentTestCards[currentCardIndex].hanzi,
          () => setIsSpeaking(true),
          () => setIsSpeaking(false)
        );
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [showCardFeedback, currentCardIndex, currentTestCards]);

  if (allCharacters.length === 0) {
    return (
      <div className="empty-state">
        <h2>No Characters Added</h2>
        <p>Add some characters to start practicing</p>
        <button className="btn btn-primary" onClick={onManageCharacters}>
          Manage Characters
        </button>
      </div>
    );
  }

  const handleAnswer = (selectedAnswer) => {
    // Satisfy browser auto-play policy
    tts.unlock();

    const currentCard = currentTestCards[currentCardIndex];
    const currentSide = sideOrder[currentSideIndex];
    const correct = selectedAnswer === currentCard[currentSide];

    if (!correct) {
      setIncorrectAnswers(prev => [...prev, {
        card: currentCard,
        side: currentSideIndex === 1 ? 'pinyin' : 'translation',
        userAnswer: selectedAnswer,
        correctAnswer: currentCard[currentSide]
      }]);
    }

    if (currentSideIndex === 1) {
      vibrate(15);
      playSound(SOUNDS.TAP);
      setCurrentCardAnswers(prev => ({ ...prev, side2: correct }));
      setCurrentSideIndex(prev => prev + 1);
    } else {
      vibrate(30);
      playSound(SOUNDS.SUCCESS);
      setCurrentCardAnswers(prev => ({ ...prev, side3: correct }));
      setShowCardFeedback(true);
    }

    if (correct) {
      setPoints(prev => prev + 50);
    }
  };

  const handleNext = () => {
    vibrate(15);
    playSound(SOUNDS.TAP);
    setShowCardFeedback(false);
    setShowCharacterOrigin(false);
    setCurrentCardAnswers({ side2: null, side3: null });

    if (currentCardIndex === currentTestCards.length - 1) {
      setShowResult(true);
    } else {
      setCurrentCardIndex(prev => prev + 1);
      setCurrentSideIndex(0);
    }
  };

  const handleRestartTest = () => {
    if (window.confirm("Are you sure you want to restart? All progress will be lost.")) {
      vibrate(30);
      playSound(SOUNDS.TAP);
      resetTest();
    }
  };

  const resetTest = (cards = allCharacters) => {
    setCurrentTestCards(getRandomCards(cards));
    setCurrentCardIndex(0);
    setCurrentSideIndex(0);
    setPoints(0);
    setShowResult(false);
    setShowCardFeedback(false);
    setCurrentCardAnswers({ side2: null, side3: null });
    setIncorrectAnswers([]);
    setShowIncorrectAnswers(false);
    setIsPracticeMode(false);
  };

  const startPracticeIncorrect = () => {
    const uniqueIncorrectCards = Array.from(
      new Set(incorrectAnswers.map(item => item.card.id))
    ).map(id =>
      incorrectAnswers.find(item => item.card.id === id).card
    );

    if (uniqueIncorrectCards.length > 0) {
      playSound(SOUNDS.TAP);
      setIsPracticeMode(true);
      resetTest(uniqueIncorrectCards);
    }
  };

  const submitChanges = async () => {
    playSound(SOUNDS.TAP);
    try {
      const docRef = doc(db, 'userSettings', userId);
      await setDoc(docRef, { sideOrder: tempSideOrder }, { merge: true });
      setSideOrder(tempSideOrder);
      setIsOrderModalOpen(false);
      resetTest();
    } catch (submitError) {
      console.error('Error updating settings:', submitError);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setTempSideOrder((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  if (showResult) {
    return (
      <div className="result-container">
        <h2>Test Complete!</h2>
        <p>The result: {points} points out of {currentTestCards.length}</p>
        <div className="buttons-container">
          {incorrectAnswers.length > 0 && (
            <>
              {showIncorrectAnswers ? (
                <div className="incorrect-answers-container">
                  <div className="incorrect-answers">
                    {incorrectAnswers.map((item, index) => (
                      <div className="incorrect-item" key={index}>
                        <div className="card-info">
                          <span className="character">{item.card.hanzi}</span>
                          <span className="divider">•</span>
                          <span className="pinyin">{item.card.pinyin}</span>
                          <span className="divider">•</span>
                          <span className={`translation ${isTranslationExplanation(item.card) ? 'explanation-text' : ''}`}>
                            {item.card.translation}
                          </span>
                        </div>
                        <div className="answer-comparison">
                          <div className="incorrect-answer">
                            Your answer: <span className="wrong">{item.userAnswer}</span>
                          </div>
                          <div className="correct-answer">
                            Correct {item.side}: <span className="right">{item.correctAnswer}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={startPracticeIncorrect}
                  >
                    Practice These Cards
                  </button>
                </div>
              ) : (
                <>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      playSound(SOUNDS.TAP);
                      setShowIncorrectAnswers(true);
                    }}
                  >
                    Show Incorrect Answers ({incorrectAnswers.length})
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={startPracticeIncorrect}
                  >
                    Practice Incorrect Cards
                  </button>
                </>
              )}
            </>
          )}
          <button
            className="btn btn-primary"
            onClick={() => {
              playSound(SOUNDS.TAP);
              resetTest();
            }}
          >
            Start New Test{isPracticeMode ? ' (All Cards)' : ''}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="deck">
        <div className="progress-counter">
          Card {currentCardIndex + 1}/{currentTestCards.length}
          {isPracticeMode && (
            <span className="practice-mode-indicator"> (Practice Mode)</span>
          )}
        </div>

        {currentSideIndex === 0 ? (
          <>
            <h3 className="card-label">{sideOrder[currentSideIndex].charAt(0).toUpperCase() + sideOrder[currentSideIndex].slice(1)}</h3>
            <Flashcard
              data={currentTestCards[currentCardIndex]}
              currentSide={sideOrder[currentSideIndex]}
              onFlip={() => {
                playSound(SOUNDS.FLIP);
                setCurrentSideIndex(prev => prev + 1);
              }}
            />
            <button className="btn btn-secondary restart-btn" onClick={() => handleRestartTest()}>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"></path>
                <polyline points="21 3 21 8 16 8"></polyline>
              </svg>
              <span>Restart Test</span>
            </button>
          </>
        ) : showCardFeedback ? (
          <div className="card-feedback">
            <h3>Card Summary</h3>
            <div
              className={`card-info ${isSpeaking ? 'speaking' : ''}`}
              onClick={() => tts.speak(
                currentTestCards[currentCardIndex].hanzi,
                () => setIsSpeaking(true),
                () => setIsSpeaking(false)
              )}
            >
              {currentTestCards[currentCardIndex].hanzi} / {currentTestCards[currentCardIndex].pinyin} /{' '}
              <span className={isTranslationExplanation(currentTestCards[currentCardIndex]) ? 'explanation-text' : ''}>
                {currentTestCards[currentCardIndex].translation}
              </span>
            </div>
            <div className="feedback-section top">
              <p>Pinyin: {currentCardAnswers.side2 ? "50 points" : "0 points"}</p>
              {currentCardAnswers.side2 ? null : (
                <p className="correct-answer">
                  Correct answer:{' '}
                  <span className={isTranslationExplanation(currentTestCards[currentCardIndex], sideOrder[1]) ? 'explanation-text' : ''}>
                    {currentTestCards[currentCardIndex][sideOrder[1]]}
                  </span>
                </p>
              )}
            </div>
            <div className="feedback-section bottom">
              <p>Translation: {currentCardAnswers.side3 ? "50 points" : "0 points"}</p>
              {currentCardAnswers.side3 ? null : (
                <p className="correct-answer">
                  Correct answer:{' '}
                  <span className={isTranslationExplanation(currentTestCards[currentCardIndex], sideOrder[2]) ? 'explanation-text' : ''}>
                    {currentTestCards[currentCardIndex][sideOrder[2]]}
                  </span>
                </p>
              )}
            </div>
            <button
              className="btn character-origin-btn"
              onClick={() => {
                playSound(SOUNDS.TAP);
                setShowCharacterOrigin(true);
              }}
            >
              Character Origin
            </button>
            <button className="btn btn-primary" onClick={handleNext} style={{ width: '100%' }}>
              {currentCardIndex === currentTestCards.length - 1 ? "Show Results" : "Next Card"}
            </button>
          </div>
        ) : (
          <div className="multiple-choice">
            <h3>Choose the correct {sideOrder[currentSideIndex]}:</h3>
            <div className="options-container">
              {options.map((option, index) => (
                <button
                  key={index}
                  className={`option-btn ${isExplanationOption(option) ? 'explanation-option' : ''}`}
                  onClick={() => handleAnswer(option)}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {isOrderModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content" ref={modalRef}>
            <button className="close-icon" onClick={() => {
              playSound(SOUNDS.TAP);
              closeModal();
            }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <h2>Change Flashcard Order</h2>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={tempSideOrder}
                strategy={verticalListSortingStrategy}
              >
                <ul className="order-list">
                  {Array.isArray(tempSideOrder) && tempSideOrder.map((id) => (
                    <SortableItem key={id} id={id} />
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
            <button className="btn btn-primary submit-btn" onClick={submitChanges}>
              Submit
            </button>
          </div>
        </div>
      )}

      {showCharacterOrigin && (
        <CharacterOrigin
          character={currentTestCards[currentCardIndex]}
          onClose={() => setShowCharacterOrigin(false)}
        />
      )}
    </>
  );
};

export default Deck;
